import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  FolderOpen,
  Pencil,
  Plus,
  Search,
  Trash2,
  Users,
  AlertTriangle,
  Check,
  X,
} from "lucide-react";
import advisorGroupService, {
  AdvisorGroupDetail,
  AdvisorGroupMember,
  MemberSyncOutcome,
  ProductWeight,
} from "../../services/advisorGroupService";
import advisorClientService, {
  AdvisorClientListItem,
} from "../../services/advisorClientService";
import { useAuth } from "../../contexts/AuthContext";
import ConfirmationModal from "../../components/ConfirmationModal";
import GroupStrategyEditor from "../../components/advisor/GroupStrategyEditor";
import { useDiscoverProducts } from "../../hooks/useDiscoverProducts";
import { buildProductLookup } from "../../utils/strategyUtils";

const AdvisorGroupDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { advisorAccountId: advisorAccountIdParam, groupId: groupIdParam } = useParams<{
    advisorAccountId: string;
    groupId: string;
  }>();
  const advisorAccountId = Number(advisorAccountIdParam);
  const groupId = Number(groupIdParam);
  const { user } = useAuth();

  const userRoles = user?.userRoles?.map((ur: { role: string }) => ur.role) || [];
  const hasAdvisorRole = userRoles.includes("ADVISOR");

  const [group, setGroup] = useState<AdvisorGroupDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Rename state
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameName, setRenameName] = useState("");
  const [renameLoading, setRenameLoading] = useState(false);

  // Add member state
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [allClients, setAllClients] = useState<AdvisorClientListItem[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [addMemberSelection, setAddMemberSelection] = useState<Set<string>>(new Set());
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  const [addMemberError, setAddMemberError] = useState<string | null>(null);
  const [memberSearchQuery, setMemberSearchQuery] = useState("");

  // Remove member state
  const [removeMemberTarget, setRemoveMemberTarget] = useState<AdvisorGroupMember | null>(null);
  const [removeMemberLoading, setRemoveMemberLoading] = useState(false);

  // Sync outcome state (from strategy sync or membership changes)
  const [syncOutcomes, setSyncOutcomes] = useState<MemberSyncOutcome[]>([]);
  const [showSyncResults, setShowSyncResults] = useState(false);

  // Strategy editor state
  const [isEditingStrategy, setIsEditingStrategy] = useState(false);
  const [showRemoveStrategyConfirm, setShowRemoveStrategyConfirm] = useState(false);
  const [removeStrategyLoading, setRemoveStrategyLoading] = useState(false);

  const { allItems: productItems, isLoading: productsLoading } = useDiscoverProducts(
    hasAdvisorRole ? undefined : { admin: true }
  );
  const productLookup = React.useMemo(
    () => buildProductLookup(productItems),
    [productItems]
  );

  const failedOutcomes = syncOutcomes.filter((o) => !o.success);

  const fetchGroup = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const detail = hasAdvisorRole
        ? await advisorGroupService.getGroup(groupId)
        : await advisorGroupService.getGroupAdmin(advisorAccountId, groupId);
      setGroup(detail);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || "Failed to load group";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [groupId, hasAdvisorRole, advisorAccountId]);

  const fetchClients = useCallback(async () => {
    try {
      setClientsLoading(true);
      let clientList: AdvisorClientListItem[];
      if (hasAdvisorRole) {
        ({ clients: clientList } = await advisorClientService.listClients());
      } else {
        ({ clients: clientList } = await advisorClientService.listClientsAdmin(advisorAccountId));
      }
      setAllClients(clientList);
    } catch {
      // Non-critical
    } finally {
      setClientsLoading(false);
    }
  }, [hasAdvisorRole, advisorAccountId]);

  useEffect(() => {
    fetchGroup();
  }, [fetchGroup]);

  // -- Rename handlers --

  const startRenaming = () => {
    if (!group) return;
    setRenameName(group.name);
    setIsRenaming(true);
  };

  const cancelRenaming = () => {
    setIsRenaming(false);
    setRenameName("");
  };

  const handleRename = async () => {
    if (!group || !renameName.trim() || renameName.trim() === group.name) {
      cancelRenaming();
      return;
    }
    try {
      setRenameLoading(true);
      const renamePayload = { name: renameName.trim() };
      if (hasAdvisorRole) {
        await advisorGroupService.updateGroup(group.id, renamePayload);
      } else {
        await advisorGroupService.updateGroupAdmin(advisorAccountId, group.id, renamePayload);
      }
      await fetchGroup();
      setIsRenaming(false);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to rename group");
    } finally {
      setRenameLoading(false);
    }
  };

  // -- Add member handlers --

  const openAddMemberModal = () => {
    setAddMemberSelection(new Set());
    setAddMemberError(null);
    setMemberSearchQuery("");
    setShowAddMemberModal(true);
    fetchClients();
  };

  const handleAddMembers = async () => {
    if (!group || addMemberSelection.size === 0) return;
    try {
      setAddMemberLoading(true);
      setAddMemberError(null);
      const existingMemberIds = group.members.map((m) => m.userId);
      const mergedMemberIds = [...existingMemberIds, ...Array.from(addMemberSelection)];
      const addPayload = { memberUserIds: mergedMemberIds };
      const result = hasAdvisorRole
        ? await advisorGroupService.updateGroup(group.id, addPayload)
        : await advisorGroupService.updateGroupAdmin(advisorAccountId, group.id, addPayload);

      if (result.memberSyncOutcomes.length > 0) {
        setSyncOutcomes(result.memberSyncOutcomes);
        const hasFailures = result.memberSyncOutcomes.some((o) => !o.success);
        setShowSyncResults(hasFailures);
      }

      setShowAddMemberModal(false);
      await fetchGroup();
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || "Failed to add members";
      setAddMemberError(message);
    } finally {
      setAddMemberLoading(false);
    }
  };

  // -- Remove member handler --

  const handleRemoveMember = async () => {
    if (!group || !removeMemberTarget) return;
    try {
      setRemoveMemberLoading(true);
      const remainingMemberIds = group.members
        .filter((m) => m.userId !== removeMemberTarget.userId)
        .map((m) => m.userId);
      const removePayload = { memberUserIds: remainingMemberIds };
      if (hasAdvisorRole) {
        await advisorGroupService.updateGroup(group.id, removePayload);
      } else {
        await advisorGroupService.updateGroupAdmin(advisorAccountId, group.id, removePayload);
      }
      setRemoveMemberTarget(null);
      await fetchGroup();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to remove member");
      setRemoveMemberTarget(null);
    } finally {
      setRemoveMemberLoading(false);
    }
  };

  // -- Strategy re-sync handler --

  const handleResyncStrategy = async () => {
    if (!group || !group.strategy) return;
    try {
      const strategyPayload = { strategies: group.strategy };
      const result = hasAdvisorRole
        ? await advisorGroupService.updateStrategy(group.id, strategyPayload)
        : await advisorGroupService.updateStrategyAdmin(advisorAccountId, group.id, strategyPayload);
      setSyncOutcomes(result.memberOutcomes);
      setShowSyncResults(result.failed > 0);
      await fetchGroup();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to sync strategy");
    }
  };

  // -- Strategy editor handlers --

  const handleStrategySave = async (strategies: ProductWeight[]) => {
    if (!group) return;
    const strategyPayload = { strategies };
    const result = hasAdvisorRole
      ? await advisorGroupService.updateStrategy(group.id, strategyPayload)
      : await advisorGroupService.updateStrategyAdmin(advisorAccountId, group.id, strategyPayload);
    setSyncOutcomes(result.memberOutcomes);
    setShowSyncResults(result.failed > 0);
    setIsEditingStrategy(false);
    await fetchGroup();
  };

  const handleRemoveStrategy = async () => {
    if (!group) return;
    try {
      setRemoveStrategyLoading(true);
      const clearPayload = { strategies: [] as ProductWeight[] };
      const result = hasAdvisorRole
        ? await advisorGroupService.updateStrategy(group.id, clearPayload)
        : await advisorGroupService.updateStrategyAdmin(advisorAccountId, group.id, clearPayload);
      setSyncOutcomes(result.memberOutcomes);
      setShowSyncResults(result.failed > 0);
      setShowRemoveStrategyConfirm(false);
      await fetchGroup();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to remove strategy");
      setShowRemoveStrategyConfirm(false);
    } finally {
      setRemoveStrategyLoading(false);
    }
  };

  const ungroupedClients = allClients.filter((c) => c.groupId === null);
  const filteredUngroupedClients = ungroupedClients.filter((client) => {
    if (!memberSearchQuery) return true;
    const query = memberSearchQuery.toLowerCase();
    return (
      client.name?.toLowerCase().includes(query) ||
      client.email.toLowerCase().includes(query) ||
      client.ibkrAccountId?.toLowerCase().includes(query)
    );
  });

  const memberCount = group?.members.length ?? 0;

  if (loading) {
    return (
      <div className="p-6 md:p-8 max-w-[1400px] mx-auto">
        <div className="space-y-3">
          <div className="animate-pulse bg-gray-200 dark:bg-gray-800 rounded-lg h-12 w-64" />
          <div className="animate-pulse bg-gray-200 dark:bg-gray-800 rounded-lg h-64" />
        </div>
      </div>
    );
  }

  if (error && !group) {
    return (
      <div className="p-6 md:p-8 max-w-[1400px] mx-auto">
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          <button
            onClick={fetchGroup}
            className="mt-2 text-sm text-red-600 dark:text-red-400 underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!group) return null;

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto">
      {/* Back + Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/advisor/${advisorAccountId}/groups`)}
          className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-rp-amber transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Groups
        </button>

        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 dark:bg-rp-surface-raised">
            <FolderOpen className="w-5 h-5 text-rp-amber" />
          </div>
          {isRenaming ? (
            <div className="flex items-center gap-2 flex-1">
              <input
                type="text"
                value={renameName}
                onChange={(e) => setRenameName(e.target.value)}
                className="px-3 py-1.5 bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator rounded-lg text-lg font-bold text-gray-900 dark:text-white focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber transition-all"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleRename();
                  if (e.key === "Escape") cancelRenaming();
                }}
              />
              <button
                onClick={handleRename}
                disabled={renameLoading}
                className="p-1.5 rounded-lg text-green-500 hover:bg-green-500/10 transition-colors disabled:opacity-50"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                onClick={cancelRenaming}
                disabled={renameLoading}
                className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-500/10 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold tracking-tight text-white">{group.name}</h1>
              <button
                onClick={startRenaming}
                className="p-1.5 rounded-lg text-gray-400 hover:text-rp-amber hover:bg-rp-amber/10 transition-colors"
                title="Rename group"
              >
                <Pencil className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
        <p className="text-sm text-gray-400 mt-2 ml-[52px]">
          {memberCount} member{memberCount !== 1 ? "s" : ""}
          <span className="mx-2 text-rp-separator">|</span>
          Created {new Date(group.createdAtUtc).toLocaleDateString()}
        </p>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Sync failures banner */}
      {showSyncResults && failedOutcomes.length > 0 && (
        <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex items-start gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5 shrink-0" />
            <p className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
              {failedOutcomes.length} member{failedOutcomes.length !== 1 ? "s" : ""} failed to sync
            </p>
          </div>
          <ul className="ml-6 space-y-1">
            {failedOutcomes.map((outcome) => (
              <li key={outcome.userId} className="text-xs text-yellow-600 dark:text-yellow-400">
                {outcome.userId}: {outcome.error}
              </li>
            ))}
          </ul>
          <button
            onClick={() => setShowSyncResults(false)}
            className="mt-2 text-xs text-yellow-600 dark:text-yellow-400 underline hover:no-underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Members Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Members</h2>
          <button
            onClick={openAddMemberModal}
            className="flex items-center gap-1.5 px-3 py-2 bg-rp-amber hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors text-sm"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Members
          </button>
        </div>

        {group.members.length === 0 ? (
          <div className="py-12 bg-white dark:bg-rp-surface rounded-xl border border-gray-200 dark:border-rp-separator flex flex-col items-center justify-center">
            <Users className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">No members in this group yet.</p>
            <button
              onClick={openAddMemberModal}
              className="px-4 py-2 bg-rp-amber hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors text-sm"
            >
              Add Members
            </button>
          </div>
        ) : (
          <div className="bg-white dark:bg-rp-surface rounded-xl border border-gray-200 dark:border-rp-separator overflow-hidden">
            <div className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-gray-200 dark:border-rp-separator text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              <div className="col-span-5">Name</div>
              <div className="col-span-6">Email</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>
            {group.members.map((member) => (
              <div
                key={member.userId}
                className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-gray-200 dark:border-rp-separator last:border-b-0 hover:bg-gray-50 dark:hover:bg-rp-surface-raised transition-colors"
              >
                <div className="col-span-5 flex items-center">
                  <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {member.name || "Unnamed"}
                  </span>
                </div>
                <div className="col-span-6 flex items-center">
                  <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {member.email}
                  </span>
                </div>
                <div className="col-span-1 flex items-center justify-end">
                  <button
                    onClick={() => setRemoveMemberTarget(member)}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-500/10 transition-colors"
                    title="Remove from group"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Strategy Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Strategy Template</h2>
          {!isEditingStrategy && (
            <div className="flex items-center gap-2">
              {group.strategy !== null && (
                <>
                  <button
                    onClick={() => setShowRemoveStrategyConfirm(true)}
                    className="px-3 py-1.5 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg transition-colors"
                  >
                    Remove Strategy
                  </button>
                  <button
                    onClick={handleResyncStrategy}
                    className="px-3 py-1.5 text-xs text-rp-amber hover:text-yellow-600 border border-gray-200 dark:border-rp-separator rounded-lg font-medium transition-colors"
                  >
                    Re-sync
                  </button>
                </>
              )}
              <button
                onClick={() => setIsEditingStrategy(true)}
                disabled={productsLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-rp-amber hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors text-xs disabled:opacity-50"
              >
                <Pencil className="w-3 h-3" />
                {group.strategy !== null ? "Edit" : "Add Strategy"}
              </button>
            </div>
          )}
        </div>

        {isEditingStrategy ? (
          <GroupStrategyEditor
            strategy={group.strategy}
            productItems={productItems}
            productLookup={productLookup}
            memberCount={group.members.length}
            onSave={handleStrategySave}
            onCancel={() => setIsEditingStrategy(false)}
          />
        ) : group.strategy !== null ? (
          <div className="bg-white dark:bg-rp-surface rounded-xl border border-gray-200 dark:border-rp-separator overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-rp-separator">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {group.strategy.length === 0
                  ? "Full liquidation (empty template)"
                  : `${group.strategy.length} allocation${group.strategy.length !== 1 ? "s" : ""}`}
              </span>
            </div>
            {group.strategy.length > 0 && (
              <>
                <div className="grid grid-cols-12 gap-4 px-4 py-2.5 border-b border-gray-200 dark:border-rp-separator text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  <div className="col-span-2">Type</div>
                  <div className="col-span-2">Product ID</div>
                  <div className="col-span-4">Product</div>
                  <div className="col-span-2">Direction</div>
                  <div className="col-span-2 text-right">Weight</div>
                </div>
                {group.strategy.map((pw: ProductWeight) => {
                  const lookupKey = `${pw.entityType}-${pw.productId}` as const;
                  const productEntry = productLookup.get(lookupKey as any);
                  const displayName = productEntry?.title ?? `${pw.entityType} #${pw.productId}`;
                  return (
                    <div
                      key={`${pw.entityType}-${pw.productId}`}
                      className="grid grid-cols-12 gap-4 px-4 py-2.5 border-b border-gray-200 dark:border-rp-separator last:border-b-0"
                    >
                      <div className="col-span-2 flex items-center">
                        <span className="px-2 py-0.5 bg-gray-100 dark:bg-rp-surface-raised text-gray-600 dark:text-gray-400 text-[10px] font-semibold uppercase tracking-wider rounded border border-gray-200 dark:border-gray-800">
                          {pw.entityType}
                        </span>
                      </div>
                      <div className="col-span-2 flex items-center">
                        <span className="text-sm font-mono text-gray-900 dark:text-white">
                          {pw.productId}
                        </span>
                      </div>
                      <div className="col-span-4 flex items-center">
                        <span className="text-sm text-gray-900 dark:text-white truncate">
                          {displayName}
                        </span>
                      </div>
                      <div className="col-span-2 flex items-center">
                        <span className={`text-xs font-medium ${
                          pw.position === "LONG"
                            ? "text-green-600 dark:text-green-500"
                            : "text-red-600 dark:text-red-500"
                        }`}>
                          {pw.position}
                        </span>
                      </div>
                      <div className="col-span-2 flex items-center justify-end">
                        <span className="text-sm font-mono text-gray-900 dark:text-white">
                          {pw.weight}%
                        </span>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        ) : (
          <div className="py-12 bg-white dark:bg-rp-surface rounded-xl border border-gray-200 dark:border-rp-separator flex flex-col items-center justify-center">
            <FolderOpen className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              No strategy template configured for this group.
            </p>
            <button
              onClick={() => setIsEditingStrategy(true)}
              disabled={productsLoading}
              className="px-4 py-2 bg-rp-amber hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors text-sm disabled:opacity-50"
            >
              {productsLoading ? "Loading products..." : "Add Strategy"}
            </button>
          </div>
        )}
      </div>

      {/* Remove Strategy Confirmation */}
      <ConfirmationModal
        isOpen={showRemoveStrategyConfirm}
        onClose={() => { if (!removeStrategyLoading) setShowRemoveStrategyConfirm(false); }}
        onConfirm={handleRemoveStrategy}
        title="Remove Strategy Template"
        message="This will clear the strategy template for this group and sync an empty strategy (full liquidation) to all members. Are you sure?"
        confirmText="Remove Strategy"
        isDestructive
        loading={removeStrategyLoading}
      />

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => { if (!addMemberLoading) setShowAddMemberModal(false); }}
          />
          <div className="relative bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator rounded-lg shadow-lg w-full max-w-md mx-4 p-6 max-h-[70vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Add Members</h3>
              <button
                onClick={() => { if (!addMemberLoading) setShowAddMemberModal(false); }}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-rp-separator transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Only ungrouped clients are shown. Clients in other groups must be removed first.
            </p>

            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
              <input
                type="text"
                placeholder="Search clients..."
                value={memberSearchQuery}
                onChange={(e) => setMemberSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator rounded-lg text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber transition-all"
              />
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto border border-gray-200 dark:border-rp-separator rounded-lg mb-4">
              {clientsLoading && (
                <div className="p-4 text-center text-xs text-gray-400">Loading clients...</div>
              )}
              {!clientsLoading && filteredUngroupedClients.length === 0 && (
                <div className="p-4 text-center text-xs text-gray-400">
                  {ungroupedClients.length === 0
                    ? "No ungrouped clients available."
                    : "No clients match your search."}
                </div>
              )}
              {filteredUngroupedClients.map((client) => {
                const isSelected = addMemberSelection.has(client.userId);
                return (
                  <label
                    key={client.userId}
                    className="flex items-center gap-3 px-3 py-2.5 border-b border-gray-200 dark:border-rp-separator last:border-b-0 hover:bg-gray-50 dark:hover:bg-rp-surface cursor-pointer transition-colors"
                    onClick={() => {
                      setAddMemberSelection((prev) => {
                        const next = new Set(prev);
                        if (next.has(client.userId)) next.delete(client.userId);
                        else next.add(client.userId);
                        return next;
                      });
                    }}
                  >
                    <div
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                        isSelected
                          ? "bg-rp-amber border-rp-amber"
                          : "border-gray-300 dark:border-rp-separator hover:border-rp-amber"
                      }`}
                    >
                      {isSelected && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 dark:text-white truncate">
                        {client.name || "Unnamed"}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {client.email}
                        {client.ibkrAccountId && (
                          <span className="font-mono ml-2">{client.ibkrAccountId}</span>
                        )}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>

            {addMemberError && (
              <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-700 dark:text-red-400">{addMemberError}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                type="button"
                disabled={addMemberLoading}
                onClick={() => setShowAddMemberModal(false)}
                className="px-4 py-2.5 bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={addMemberLoading || addMemberSelection.size === 0}
                onClick={handleAddMembers}
                className="px-4 py-2.5 bg-rp-amber hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addMemberLoading
                  ? "Adding..."
                  : `Add ${addMemberSelection.size || ""} Member${addMemberSelection.size !== 1 ? "s" : ""}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Member Confirmation */}
      <ConfirmationModal
        isOpen={!!removeMemberTarget}
        onClose={() => { if (!removeMemberLoading) setRemoveMemberTarget(null); }}
        onConfirm={handleRemoveMember}
        title="Remove Member"
        message={`Remove "${removeMemberTarget?.name || removeMemberTarget?.email}" from this group? They will become ungrouped.`}
        confirmText="Remove"
        isDestructive
        loading={removeMemberLoading}
      />
    </div>
  );
};

export default AdvisorGroupDetailPage;
